import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { ArrowLeftOutlined, DownloadOutlined, PrinterOutlined } from "@ant-design/icons";
import { App, Button, Card, Flex, Grid, Tabs, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { ACCOUNT_TYPE_MAP } from "./components/AccountTransactionConfig";
import UniversalLedgerFilters from "./components/AccountTransactionFilters";
import UniversalLedgerTable from "./components/AccountTransactionTable";
import ReportDom from "../../../components/common/ReportDom";
import { exportLedgerCsv } from "./components/exportLedgerCsv";

const { Title } = Typography;

const fmt = (num) =>
  Number(num || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// ── Labels for the linked party tab ──────────────────────────────────────────
// When viewing a debitor (customer), linked party is a supplier and vice versa
const LINKED_LABEL = {
  debitor:  { main: "Customer",  linked: "Supplier" },
  creditor: { main: "Supplier",  linked: "Customer" },
};

const UniversalLedger = () => {
  const { message } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens   = useBreakpoint();
  const navigate  = useNavigate();
  const reportRef = useRef(null);

  const {
    list,
    filters,
    linkedParty,
    closingBalance,
    openingBalance,
    linkedClosingBalance,
    linkedLedgerList,
    linkedOpeningBalance,
  } = useSelector((state) => state.universalLedger);

  const typeLabel   = ACCOUNT_TYPE_MAP[filters.accTypeId]?.label || "Account";
  const reportTitle = filters.accName
    ? `${typeLabel} — ${filters.accName}`
    : "Account Transaction Ledger";

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: reportTitle,
    removeAfterPrint: true,
  });
const handleDownloadCsv = () => {
  const safeName = (filters.accName || "account").replace(/[^a-z0-9]/gi, "_");
  exportLedgerCsv({
    list,
    openingBalance,
    closingBalance,
    filename: `${typeLabel}_${safeName}`,
    title: reportTitle,
  });
};

const handleDownloadLinkedCsv = () => {
  const safeName = (filters.accName || "account").replace(/[^a-z0-9]/gi, "_");
  const linkedLabel = LINKED_LABEL[filters.accTypeId]?.linked || "Linked";
  exportLedgerCsv({
    list:           linkedLedgerList,
    openingBalance: linkedOpeningBalance,
    closingBalance: linkedClosingBalance,
    filename:       `${linkedLabel}_${safeName}`,
    title:          `${linkedLabel} Ledger — ${filters.accName}`,
  });
};
  // Labels for net balance card and tabs
  const labels      = LINKED_LABEL[filters.accTypeId];
  const showLinked  = !!linkedParty && list.length > 0;

  // Net balance direction depends on account type
  // debitor:  customer owes you (closingBalance) minus supplier you owe (linkedClosingBalance)
  // creditor: customer owes you (linkedClosingBalance) minus supplier you owe (closingBalance)
  const netBalance = filters.accTypeId === "debitor"
    ? closingBalance - linkedClosingBalance
    : linkedClosingBalance - closingBalance;

  const mainColor   = filters.accTypeId === "debitor" ? "#1890ff" : "#ff4d4f";
  const linkedColor = filters.accTypeId === "debitor" ? "#ff4d4f" : "#1890ff";

  return (
    <div className="p-4">
      <App>
        {/* ── Header ───────────────────────────────────────────── */}
        <Card
          className="shadow-md rounded-xl mb-4"
          title={
            <div style={{ display: "flex", alignItems: "center" }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/reports")}
                style={{ marginRight: 8 }}
              />
              <Title level={4} style={{ margin: 0 }} className="reportBigTitle">
                Account Transaction Ledger
              </Title>
            </div>
          }
     extra={
  list.length > 0 && (
    <Flex gap={8}>
      {/* CSV download — main ledger */}
      <Button icon={<DownloadOutlined />} onClick={handleDownloadCsv}>
        {!screens.xs && "CSV"}
      </Button>

      {/* CSV download — linked ledger (only when linked tab exists) */}
      {showLinked && (
        <Button icon={<DownloadOutlined />} onClick={handleDownloadLinkedCsv}>
          {!screens.xs && "Linked CSV"}
        </Button>
      )}

      <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
        {!screens.xs && "Print"}
      </Button>
    </Flex>
  )
}
          styles={{
            header: { borderBottom: "none", padding: "12px 16px" },
            body:   { padding: 0 },
          }}
        />

        {/* ── Filters ──────────────────────────────────────────── */}
        <UniversalLedgerFilters messageApi={message} />

        {/* ── Net balance card (only for linked debitor/creditor) ─ */}
        {showLinked && labels && (
          <Card
            className="shadow-md rounded-xl mb-4"
            style={{ background: "#f0f5ff", borderColor: "#adc6ff" }}
            bodyStyle={{ padding: "16px 24px" }}
          >
            <Flex gap={40} wrap="wrap" align="center">
              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>
                  {labels.main} Balance
                </div>
                <div style={{ fontSize: 11, color: mainColor, marginBottom: 4 }}>
                  {filters.accTypeId === "debitor" ? "They owe you" : "You owe them"}
                </div>
                <div style={{ fontWeight: 700, fontSize: 20, color: mainColor }}>
                  {fmt(closingBalance)}
                </div>
              </div>

              <div style={{ fontSize: 20, color: "#ccc" }}>—</div>

              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>
                  {labels.linked} Balance
                </div>
                <div style={{ fontSize: 11, color: linkedColor, marginBottom: 4 }}>
                  {filters.accTypeId === "debitor" ? "You owe them" : "They owe you"}
                </div>
                <div style={{ fontWeight: 700, fontSize: 20, color: linkedColor }}>
                  {fmt(linkedClosingBalance)}
                </div>
              </div>

              <div style={{ fontSize: 20, color: "#ccc" }}>=</div>

              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>Net Balance</div>
                <div style={{
                  fontSize: 11,
                  color: netBalance >= 0 ? "#52c41a" : "#ff4d4f",
                  marginBottom: 4,
                }}>
                  {netBalance >= 0 ? "In your favor" : "You owe them"}
                </div>
                <div style={{
                  fontWeight: 700,
                  fontSize: 20,
                  color: netBalance >= 0 ? "#52c41a" : "#ff4d4f",
                }}>
                  {fmt(Math.abs(netBalance))}
                </div>
              </div>

              <div style={{ marginLeft: "auto", fontSize: 12, color: "#888" }}>
                {labels.linked} A/C: <strong>{linkedParty.linked_acc_code}</strong>
              </div>
            </Flex>
          </Card>
        )}

        {/* ── Table — tabs if linked, plain card if not ──────────── */}
        {showLinked && labels ? (
          <Card className="shadow-md rounded-xl">
            <Tabs
              defaultActiveKey="main"
              size="middle"
              items={[
                {
                  key:   "main",
                  label: (
                    <span>
                      {labels.main} Ledger
                      <span style={{ marginLeft: 8, fontSize: 12, color: mainColor, fontWeight: 600 }}>
                        {fmt(closingBalance)}
                      </span>
                    </span>
                  ),
                  children: <UniversalLedgerTable />,
                },
                {
                  key:   "linked",
                  label: (
                    <span>
                      {labels.linked} Ledger
                      <span style={{ marginLeft: 8, fontSize: 12, color: linkedColor, fontWeight: 600 }}>
                        {fmt(linkedClosingBalance)}
                      </span>
                    </span>
                  ),
                  children: (
                    <UniversalLedgerTable
                      externalList={linkedLedgerList}
                      externalOpening={linkedOpeningBalance}
                      externalClosing={linkedClosingBalance}
                    />
                  ),
                },
              ]}
            />
          </Card>
        ) : (
          <Card className="shadow-md rounded-xl">
            <UniversalLedgerTable />
          </Card>
        )}
      </App>

      {/* ── Hidden print layout ───────────────────────────────── */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title={reportTitle}
          mode="report-page"
          filters={filters}
          renderTable={() => (
            <UniversalLedgerTable pagination={false} printMode={true} />
          )}
        />
      </div>
    </div>
  );
};

export default UniversalLedger;